import { AfterViewInit, ChangeDetectorRef, Component, inject, OnDestroy, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { combineLatest, Observable, Subscription } from 'rxjs';

import { promiseWithCatch, safelyDetectChanges, unsubscribeAll } from 'src/app/common/app.helpers';
import { FormValueService } from 'src/app/common/guards/save-data-guard/form-value.service';
import { IDeactivate } from 'src/app/common/guards/save-data-guard/save-data.interface';
import { OperationInfoService } from 'src/app/common/long-operations/operation-info.service';
import ProjectLinks from 'src/app/common/project.links';
import { FormConstructorComponent } from 'src/app/forms/form-constructors/form-constructor/form-constructor.component';
import { FormStateService } from 'src/app/forms/form-state.service';
import { FormConstructorContent } from 'src/app/interfaces/form-and-field.interface';
import { CreateItemInfo } from 'src/app/interfaces/views.interface';
import { Post } from 'src/app/swagger-api';
import { CreatePostService } from './create-post.service';

@Component({
  selector: 'app-create-post',
  templateUrl: './create-post.component.html',
  styleUrls: ['./create-post.component.scss'],
  providers: [CreatePostService],
})
export class CreatePostComponent implements AfterViewInit, OnDestroy, IDeactivate {
  public isFormChanged = false;

  @ViewChild(FormConstructorComponent, { static: false }) private createPostForm: FormConstructorComponent;

  private subscriptions$: Subscription[] = [];
  private redirectUrl: string = ProjectLinks.LINK_POSTS;

  private formStateService = inject(FormStateService);
  private createPostService = inject(CreatePostService);
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private cdr = inject(ChangeDetectorRef);
  private formValueService = inject(FormValueService);
  private operationInfoService = inject(OperationInfoService);

  public readonly formContent$: Observable<FormConstructorContent> = this.createPostService.createPostFormSubject;

  public constructor() {
    this.subscribeOnRouteChanges();
  }

  public ngOnDestroy(): void {
    unsubscribeAll(this.subscriptions$);
  }

  public ngAfterViewInit(): void {
    this.formValueService.saveFormValue({ ...this.createPostForm?.childForm?.form.value, project_id: '' });
    this.subscribeToControlValueChanges();
  }

  public cancel(): void {
    promiseWithCatch(this.router.navigateByUrl(this.redirectUrl));
  }

  public createPost(event: Event): void {
    this.formStateService.stopAllAction(event);

    if (this.createPostForm.childForm.isFormValid()) {
      this.operationInfoService.setupOperation(
        {
          spinner: 'button',
          button: { id: 'btn-create-post' },
          successfulMessage: 'POSTS_NEW_POST_CREATED_DESCRIPTION',
        },
        () => {
          this.formValueService.saveFormValue(this.createPostForm.childForm.form.value);
          this.createPostForm.prepareSubmitData();
          this.createPostService.createPost(this.createPostForm.childForm.formContent.data);
        },
      );
    }
  }

  private subscribeOnRouteChanges(): void {
    this.subscriptions$.push(
      this.route.params.subscribe(() => {
        const state: CreateItemInfo<Post> = this.router.lastSuccessfulNavigation?.extras?.state as CreateItemInfo<Post>;

        if (state?.data) {
          this.createPostService.fetchAttributeMetadata(state.data);
          this.redirectUrl = state.returnUrl || this.redirectUrl;
          return;
        }

        this.createPostService.fetchAttributeMetadata();
      }),
    );
  }

  private subscribeToControlValueChanges(): void {
    if (this.createPostForm?.childForm) {
      this.subscriptions$.push(
        combineLatest([this.createPostForm.childForm.form.valueChanges, this.formValueService.valueChanged]).subscribe(
          ([value, startValue]: [object, object]) => {
            this.isFormChanged = this.formValueService.formChangeDetection(value, startValue);
            safelyDetectChanges(this.cdr);
          },
        ),
      );
    }
  }
}
